import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@hortis/ui/button";
import { FormControl, FormField, FormItem, FormMessage } from "@hortis/ui/form";
import { ChevronDown } from "@hortis/ui/icons";
import type {
  NullableStringComparator,
  RecordTagFilters,
} from "generated/graphql";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import {
  CollapsableRadioButton,
  RadioButton,
  RadioGroup,
} from "src/components/radio";
import { DonorPicker } from "src/features/create-accession/components/accession-data-form/donor-picker";
import { FilterTitle } from "src/features/filters/components/modal/filter-title";
import { determineInitialOperation } from "src/features/filters/plant-materials/modals/determine-initial-operation";
import type { FilterFormProps } from "src/features/filters/plant-materials/modals/types";
import { match } from "ts-pattern";
import { z } from "zod";
import type { FilterModalProps } from "../components/modal/filter-modal";
import { FilterModal } from "../components/modal/filter-modal";
import { FilterModalButtons } from "../components/modal/modal-buttons";
import { FilterModalError } from "../components/modal/modal-error";
import { useFilterForm } from "../components/modal/use-filter-form";

type InitialValue = NullableStringComparator | null | undefined;

const resolver = zodResolver(
  z.object({
    is: z.string().nullable().optional(),
    hasNoValue: z.boolean().nullable().optional(),
  }),
);

const createDefaultValues = (initialValue: InitialValue) => {
  const defaultValues = {
    is: initialValue?.eq ?? undefined,
    hasNoValue: initialValue?.null ?? undefined,
  };
  return {
    defaultValues,
    initialOperation: determineInitialOperation(defaultValues, "is"),
  };
};

interface DonorFilterFormProps {
  addModifyFilter: (changes: NullableStringComparator) => void;
  deleteFilter: (() => void) | undefined;
  tagFilters?: RecordTagFilters;
  initialValue?: InitialValue;
}

export const DonorsFilterForm = ({
  initialValue,
  addModifyFilter,
  deleteFilter,
  onClose,
}: Omit<FilterFormProps, "dispatchFilters" | "initialValue"> &
  DonorFilterFormProps) => {
  const { error, setError, operation, handleRadioChange, formMethods } =
    useFilterForm({
      resolver,
      ...createDefaultValues(initialValue),
    });

  const onSubmit = useMemo(
    () =>
      formMethods.handleSubmit((values) => {
        match(operation)
          .with("is", () => {
            if (values.is == null) {
              setError("Enter a value to continue");
            } else {
              addModifyFilter({
                eq: values.is,
              });
              onClose();
            }
          })
          .with("hasNoValue", () => {
            addModifyFilter({
              null: true,
            });
            onClose();
          })
          .run();
      }),
    [formMethods, operation, onClose, addModifyFilter, setError],
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={onSubmit}>
        <div className="mb-4 flex flex-col gap-4">
          <FilterTitle title="Donor / supplier" deleteFilter={deleteFilter} />
          <FilterModalError error={error} />
          <RadioGroup
            value={operation}
            onChange={handleRadioChange}
            label="Operation"
          >
            {(props) => (
              <>
                <CollapsableRadioButton
                  selectedValue="is"
                  testId="donor-is-radio"
                  label="Is"
                  {...props}
                >
                  <FormField
                    control={formMethods.control}
                    name="is"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <DonorPicker data-cy="donor-is-picker" {...field}>
                            <Button
                              className="min-h-[44px] justify-between truncate px-[14px]! py-2!"
                              endIcon={ChevronDown}
                            >
                              {field.value == null || field.value === "" ? (
                                <span className="text-grey-500">
                                  Select a donor / supplier...
                                </span>
                              ) : (
                                <div className="truncate text-grey-700">
                                  {field.value}
                                </div>
                              )}
                            </Button>
                          </DonorPicker>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CollapsableRadioButton>
                <RadioButton
                  label="Has no value"
                  selectedValue="hasNoValue"
                  testId={`donor-has-no-value-radio`}
                  {...props}
                />
              </>
            )}
          </RadioGroup>
        </div>
        <FilterModalButtons onCancel={onClose} testIdPrefix="donor" />
      </form>
    </FormProvider>
  );
};

// Seperated from form component to make sure popover unmount causes state reset
export const DonorsFilterModal = ({
  open,
  onClose,
  anchorEl,
  ...props
}: Omit<FilterFormProps, "initialValue" | "dispatchFilters"> &
  FilterModalProps &
  DonorFilterFormProps) => {
  return (
    <FilterModal anchorEl={anchorEl} open={open} onClose={onClose}>
      <DonorsFilterForm onClose={onClose} {...props} />
    </FilterModal>
  );
};
